// Local Storage Keys
const STORAGE_KEYS = {
  ONBOARDING_COMPLETE: 'calm_keeper_onboarding_complete',
  LIFETIME_ACCESS: 'calm_keeper_lifetime_access',
  DAY_MODE: 'calm_keeper_day_mode',
  TODAY_DATE: 'calm_keeper_today_date',
  TASKS: 'calm_keeper_tasks',
  BRAIN_DUMPS: 'calm_keeper_brain_dumps',
  REFLECTIONS: 'calm_keeper_reflections',
  SETTINGS: 'calm_keeper_settings',
  LAST_QUOTE_INDEX: 'calm_keeper_last_quote_index',
  SAVED_RECIPES: 'calm_keeper_saved_recipes',
  CUSTOM_TASKS: 'calm_keeper_custom_tasks',
};

// Get today's date string
export const getTodayString = () => {
  return new Date().toISOString().split('T')[0];
};

// Check if onboarding is complete
export const isOnboardingComplete = () => {
  return localStorage.getItem(STORAGE_KEYS.ONBOARDING_COMPLETE) === 'true';
};

// Set onboarding complete
export const setOnboardingComplete = () => {
  localStorage.setItem(STORAGE_KEYS.ONBOARDING_COMPLETE, 'true');
};

// Check if user has lifetime access
export const hasLifetimeAccess = () => {
  return localStorage.getItem(STORAGE_KEYS.LIFETIME_ACCESS) === 'true';
};

// Set lifetime access
export const setLifetimeAccess = () => {
  localStorage.setItem(STORAGE_KEYS.LIFETIME_ACCESS, 'true');
};

// Get current day mode
export const getDayMode = () => {
  const storedDate = localStorage.getItem(STORAGE_KEYS.TODAY_DATE);
  const today = getTodayString();
  
  // Reset day mode if it's a new day
  if (storedDate !== today) {
    localStorage.setItem(STORAGE_KEYS.TODAY_DATE, today);
    localStorage.removeItem(STORAGE_KEYS.DAY_MODE);
    return null;
  }
  
  return localStorage.getItem(STORAGE_KEYS.DAY_MODE);
};

// Set day mode
export const setDayMode = (mode) => {
  localStorage.setItem(STORAGE_KEYS.TODAY_DATE, getTodayString());
  localStorage.setItem(STORAGE_KEYS.DAY_MODE, mode);
};

// Get tasks for today
export const getTasks = () => {
  const storedDate = localStorage.getItem(STORAGE_KEYS.TODAY_DATE);
  const today = getTodayString();
  
  if (storedDate !== today) {
    return [];
  }
  
  const tasks = localStorage.getItem(STORAGE_KEYS.TASKS);
  return tasks ? JSON.parse(tasks) : [];
};

// Save tasks
export const saveTasks = (tasks) => {
  localStorage.setItem(STORAGE_KEYS.TODAY_DATE, getTodayString());
  localStorage.setItem(STORAGE_KEYS.TASKS, JSON.stringify(tasks));
};

// Get brain dumps
export const getBrainDumps = () => {
  const dumps = localStorage.getItem(STORAGE_KEYS.BRAIN_DUMPS);
  return dumps ? JSON.parse(dumps) : [];
};

// Save brain dump
export const saveBrainDump = (dump) => {
  const dumps = getBrainDumps();
  const newDump = {
    id: Date.now(),
    text: dump.text,
    category: dump.category || 'uncategorized',
    createdAt: new Date().toISOString(),
    completed: false,
  };
  dumps.unshift(newDump);
  localStorage.setItem(STORAGE_KEYS.BRAIN_DUMPS, JSON.stringify(dumps));
  return newDump;
};

// Update brain dump
export const updateBrainDump = (id, updates) => {
  const dumps = getBrainDumps();
  const index = dumps.findIndex(d => d.id === id);
  if (index !== -1) {
    dumps[index] = { ...dumps[index], ...updates };
    localStorage.setItem(STORAGE_KEYS.BRAIN_DUMPS, JSON.stringify(dumps));
  }
  return dumps;
};

// Delete brain dump
export const deleteBrainDump = (id) => {
  const dumps = getBrainDumps();
  const filtered = dumps.filter(d => d.id !== id);
  localStorage.setItem(STORAGE_KEYS.BRAIN_DUMPS, JSON.stringify(filtered));
  return filtered;
};

// Get reflections
export const getReflections = () => {
  const reflections = localStorage.getItem(STORAGE_KEYS.REFLECTIONS);
  return reflections ? JSON.parse(reflections) : [];
};

// Save today's reflection
export const saveReflection = (reflection) => {
  const reflections = getReflections();
  const today = getTodayString();
  
  // Remove existing reflection for today if any
  const filtered = reflections.filter(r => r.date !== today);
  
  const newReflection = {
    date: today,
    hardThings: reflection.hardThings,
    accomplishments: reflection.accomplishments,
    dayMode: getDayMode(),
    createdAt: new Date().toISOString(),
  };
  
  filtered.unshift(newReflection);
  localStorage.setItem(STORAGE_KEYS.REFLECTIONS, JSON.stringify(filtered));
  return newReflection;
};

// Get today's reflection
export const getTodayReflection = () => {
  const reflections = getReflections();
  const today = getTodayString();
  return reflections.find(r => r.date === today);
};

// Get settings
export const getSettings = () => {
  const settings = localStorage.getItem(STORAGE_KEYS.SETTINGS);
  return settings ? JSON.parse(settings) : {
    remindersEnabled: false,
    reminderTime: '09:00',
  };
};

// Save settings
export const saveSettings = (settings) => {
  localStorage.setItem(STORAGE_KEYS.SETTINGS, JSON.stringify(settings));
};

// Get next quote index (rotating)
export const getNextQuoteIndex = (totalQuotes) => {
  const today = getTodayString();
  const storedDate = localStorage.getItem('calm_keeper_quote_date');
  const storedIndex = localStorage.getItem(STORAGE_KEYS.LAST_QUOTE_INDEX);
  
  if (storedDate !== today) {
    const newIndex = storedIndex ? (parseInt(storedIndex) + 1) % totalQuotes : 0;
    localStorage.setItem('calm_keeper_quote_date', today);
    localStorage.setItem(STORAGE_KEYS.LAST_QUOTE_INDEX, newIndex.toString());
    return newIndex;
  }
  
  return parseInt(storedIndex) || 0;
};

// Clear all data (for reset)
export const clearAllData = () => {
  Object.values(STORAGE_KEYS).forEach(key => {
    localStorage.removeItem(key);
  });
  localStorage.removeItem('calm_keeper_quote_date');
};

// Get saved recipes
export const getSavedRecipes = () => {
  const recipes = localStorage.getItem(STORAGE_KEYS.SAVED_RECIPES);
  return recipes ? JSON.parse(recipes) : [];
};

// Save a recipe
export const saveRecipe = (recipe) => {
  const recipes = getSavedRecipes();
  const newRecipe = {
    id: Date.now(),
    name: recipe.name,
    description: recipe.description,
    ingredients: recipe.ingredients_needed || recipe.ingredients || [],
    prepTime: recipe.prep_time || recipe.prepTime,
    difficulty: recipe.difficulty,
    savedAt: new Date().toISOString(),
  };
  recipes.unshift(newRecipe);
  localStorage.setItem(STORAGE_KEYS.SAVED_RECIPES, JSON.stringify(recipes));
  return newRecipe;
};

// Delete a saved recipe
export const deleteRecipe = (id) => {
  const recipes = getSavedRecipes();
  const filtered = recipes.filter(r => r.id !== id);
  localStorage.setItem(STORAGE_KEYS.SAVED_RECIPES, JSON.stringify(filtered));
  return filtered;
};

// Check if recipe is already saved (by name)
export const isRecipeSaved = (recipeName) => {
  const recipes = getSavedRecipes();
  return recipes.some(r => r.name.toLowerCase() === recipeName.toLowerCase());
};

// Get custom tasks for a day mode
export const getCustomTasks = (modeId) => {
  const customTasks = localStorage.getItem(STORAGE_KEYS.CUSTOM_TASKS);
  if (!customTasks) return null;
  const parsed = JSON.parse(customTasks);
  return parsed[modeId] || null;
};

// Save custom tasks for a day mode
export const saveCustomTasks = (modeId, tasks) => {
  const customTasks = localStorage.getItem(STORAGE_KEYS.CUSTOM_TASKS);
  const parsed = customTasks ? JSON.parse(customTasks) : {};
  parsed[modeId] = tasks;
  localStorage.setItem(STORAGE_KEYS.CUSTOM_TASKS, JSON.stringify(parsed));
};

// Reset custom tasks for a day mode (use defaults)
export const resetCustomTasks = (modeId) => {
  const customTasks = localStorage.getItem(STORAGE_KEYS.CUSTOM_TASKS);
  if (!customTasks) return;
  const parsed = JSON.parse(customTasks);
  delete parsed[modeId];
  localStorage.setItem(STORAGE_KEYS.CUSTOM_TASKS, JSON.stringify(parsed));
};

// Get all custom tasks
export const getAllCustomTasks = () => {
  const customTasks = localStorage.getItem(STORAGE_KEYS.CUSTOM_TASKS);
  return customTasks ? JSON.parse(customTasks) : {};
};

export default STORAGE_KEYS;
